/**
 * Field Editor Utilities
 *
 * Helper functions for determining field editability and default values.
 */

import { CustomFieldType } from '@/services/api/entitySyncService';
import type { FilevineSectionPayload } from '@/services/api/generated/entitySyncService';

/**
 * Type for field values in a Filevine section payload.
 * Derived from FilevineSectionPayload['updates'] value type.
 *
 * Note: We include `null` because editors can emit null for empty values
 * (e.g., clearing a date field). The caller should handle null appropriately
 * when building the final payload.
 */
export type FilevineSectionFieldValue =
  | NonNullable<FilevineSectionPayload['updates']>[string]
  | null;

/**
 * Field types that cannot be edited in the UI.
 * These require fullstack changes or are non-editable by nature.
 */
const NON_EDITABLE_FIELD_TYPES = new Set([
  CustomFieldType.PERSON, // Contact fields - need backend integration
  CustomFieldType.ATTACHMENT, // File uploads - need backend integration
  CustomFieldType.DOCUMENT, // Document references - need backend integration
  CustomFieldType.DOC_GEN_BUTTON, // Action buttons - not data fields
  CustomFieldType.DOC_GEN_AI_BUTTON, // Action buttons - not data fields
  CustomFieldType.PROJECT_LINK, // Complex link references - read-only for now
]);

/**
 * Check if a field type can be edited in the UI.
 */
export function isFieldEditable(fieldType: number): boolean {
  return !NON_EDITABLE_FIELD_TYPES.has(fieldType);
}

/**
 * Get the appropriate default value for a field type.
 */
export function getDefaultValueForType(fieldType: number): unknown {
  switch (fieldType) {
    case CustomFieldType.INTEGER:
    case CustomFieldType.CURRENCY:
    case CustomFieldType.PERCENTAGE:
      return null;
    case CustomFieldType.TEXT:
    case CustomFieldType.MULTILINE_TEXT:
      return '';
    case CustomFieldType.DATE:
    case CustomFieldType.DEADLINE:
    case CustomFieldType.DATETIME:
      return null;
    case CustomFieldType.BOOLEAN:
      return false;
    case CustomFieldType.DROPDOWN:
      return '';
    case CustomFieldType.MULTI_SELECT:
    case CustomFieldType.STRING_LIST:
      return [];
    default:
      return null;
  }
}

/**
 * Field types that use dropdown_items from field config for options.
 */
export function fieldUsesDropdownItems(fieldType: number): boolean {
  return (
    fieldType === CustomFieldType.DROPDOWN ||
    fieldType === CustomFieldType.MULTI_SELECT
  );
}

/**
 * Field types that store array values.
 */
export function fieldIsArrayType(fieldType: number): boolean {
  return (
    fieldType === CustomFieldType.MULTI_SELECT ||
    fieldType === CustomFieldType.STRING_LIST
  );
}
